import React, { useState, useEffect } from 'react';
import { Link as RouterLink, useNavigate, useLocation } from 'react-router-dom';
import { getAuth, signInWithEmailAndPassword, sendPasswordResetEmail } from 'firebase/auth';
import {
    Button, TextField, Typography, Paper, Container, Grid, Link, CircularProgress,
    Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions,
} from '@mui/material';
import PageLayout from '../components/PageLayout';
import { useAuth } from '../context/AuthContext';
import { useNotification } from '../context/NotificationContext';
import { form } from '../theme/commonStyles';

function Login() {
    const { user } = useAuth();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [resetOpen, setResetOpen] = useState(false);
    const [resetEmail, setResetEmail] = useState('');
    const [resetLoading, setResetLoading] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();
    const auth = getAuth();
    const notify = useNotification();

    useEffect(() => {
        if (user) {
            // Kwam de gebruiker via een afgeschermde route hierheen, dan daar weer naartoe.
            const from = location.state?.from?.pathname;
            if (from) {
                navigate(from, { replace: true });
                return;
            }

            const participantAuctions = user.participantIn || [];
            const adminAuctions = user.adminIn || [];
            const allAuctions = [...new Set([...participantAuctions, ...adminAuctions])];

            if (allAuctions.length === 1 && adminAuctions.length === 0) {
                navigate(`/auction/${allAuctions[0]}/my-team`);
            } else {
                navigate('/my-auctions');
            }
        }
    }, [user, navigate, location]);

    const handleLogin = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await signInWithEmailAndPassword(auth, email, password);
        } catch (err) {
            notify.error('Inloggen mislukt. Controleer e-mail en wachtwoord.');
            console.error("Login error:", err);
        } finally {
            setLoading(false);
        }
    };

    const handleOpenReset = () => {
        // Neem het al ingevulde e-mailadres mee, zodat de gebruiker het niet opnieuw hoeft te typen.
        setResetEmail(email);
        setResetOpen(true);
    };

    const handleSendReset = async () => {
        const targetEmail = resetEmail.trim();
        if (!targetEmail) {
            notify.error('Vul je e-mailadres in.');
            return;
        }
        setResetLoading(true);
        try {
            await sendPasswordResetEmail(auth, targetEmail);
            setResetOpen(false);
            // Bewust neutrale melding: onthul niet of dit e-mailadres een account heeft.
            notify.success('Als er een account bij dit e-mailadres hoort, is er een e-mail met een resetlink verstuurd.');
        } catch (err) {
            console.error('Fout bij het versturen van de resetmail:', err);
            if (err?.code === 'auth/invalid-email') {
                notify.error('Ongeldig e-mailadres.');
            } else if (err?.code === 'auth/user-not-found') {
                // Zelfde neutrale melding als bij succes, om e-mailenumeratie te voorkomen.
                setResetOpen(false);
                notify.success('Als er een account bij dit e-mailadres hoort, is er een e-mail met een resetlink verstuurd.');
            } else if (err?.code === 'auth/too-many-requests') {
                notify.error('Te veel pogingen. Probeer het later opnieuw.');
            } else {
                notify.error('Het versturen van de resetmail is mislukt.');
            }
        } finally {
            setResetLoading(false);
        }
    };

    return (
        <PageLayout>
            <Container component="main" maxWidth="xs">
                <Paper
                    component="form"
                    onSubmit={handleLogin}
                    elevation={6}
                    sx={{
                        ...form.centeredForm,
                        mt: 8,
                        p: 4,
                    }}
                >
                    <Typography component="h1" variant="h4" gutterBottom>
                        Inloggen
                    </Typography>
                    <TextField label="E-mail" type="email" fullWidth margin="normal" value={email} onChange={e => setEmail(e.target.value)} />
                    <TextField label="Wachtwoord" type="password" fullWidth margin="normal" value={password} onChange={e => setPassword(e.target.value)} />
                    <Button type="submit" variant="contained" fullWidth sx={{ mt: 3, mb: 2 }} disabled={loading}>
                        {loading ? <CircularProgress size={24} color="inherit" /> : 'Inloggen'}
                    </Button>
                    <Grid container justifyContent="space-between" spacing={1}>
                        <Grid item>
                            <Link component="button" type="button" variant="body2" onClick={handleOpenReset}>
                                Wachtwoord vergeten?
                            </Link>
                        </Grid>
                        <Grid item>
                            <Link component={RouterLink} to="/register" variant="body2">
                                Nog geen account? Registreren
                            </Link>
                        </Grid>
                    </Grid>
                </Paper>
            </Container>

            <Dialog open={resetOpen} onClose={() => setResetOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle>Wachtwoord opnieuw instellen</DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ mb: 2 }}>
                        Vul je e-mailadres in. We sturen je een e-mail met een link om een nieuw wachtwoord in te stellen.
                    </DialogContentText>
                    <TextField
                        label="E-mail"
                        type="email"
                        fullWidth
                        autoFocus
                        value={resetEmail}
                        onChange={e => setResetEmail(e.target.value)}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setResetOpen(false)} disabled={resetLoading}>Annuleren</Button>
                    <Button onClick={handleSendReset} variant="contained" disabled={resetLoading}>
                        {resetLoading ? <CircularProgress size={24} color="inherit" /> : 'Verstuur resetlink'}
                    </Button>
                </DialogActions>
            </Dialog>
        </PageLayout>
    );
}

export default Login;
